import React, { useState, useEffect } from 'react';
import { losApi } from '../../losApi';
import { toast } from '../../../hooks/use-toast';
import { errorMessage } from '../../errors';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';

// A revealed SSN hides itself: after this long, or as soon as the tab is hidden.
export const SSN_AUTO_HIDE_MS = 15000;

// SSN input for a borrower or co-borrower. The value on the loan is masked;
// SHOW asks the server (permission + audit) and shows the plain number inline.
export function SsnField({ loanId, who = 'borrower', value, onChange, testId = 'bi-ssn' }) {
  const [revealed, setRevealed] = useState(null);
  const [revealing, setRevealing] = useState(false);

  useEffect(() => { setRevealed(null); }, [loanId, who]);

  useEffect(() => {
    if (!revealed) return undefined;
    const hide = () => setRevealed(null);
    const timer = setTimeout(hide, SSN_AUTO_HIDE_MS);
    const onVisibility = () => { if (document.hidden) hide(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [revealed]);

  const toggle = async () => {
    if (revealed) { setRevealed(null); return; }
    setRevealing(true);
    try {
      const data = await losApi.revealSsn(loanId, who);
      setRevealed(data.ssn);
    } catch (e) {
      toast({ title: 'Failed to reveal SSN', description: errorMessage(e, 'Error') });
    } finally {
      setRevealing(false);
    }
  };

  return (
    <div>
      <Label className="text-xs text-slate-500">SSN</Label>
      <div className="relative mt-1">
        <Input
          type="text"
          value={revealed || value || ''}
          onChange={(e) => onChange(e.target.value)}
          readOnly={!!revealed}
          autoComplete="off"
          data-1p-ignore
          data-lpignore="true"
          placeholder="XXX-XX-XXXX"
          data-testid={testId}
        />
        <button type="button" onClick={toggle} disabled={revealing}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-[10px] font-bold">
          {revealed ? 'HIDE' : (revealing ? '…' : 'SHOW')}
        </button>
      </div>
    </div>
  );
}
